import { useState, useEffect } from 'react';
import { SurgeonLayout } from '@/components/surgeon/SurgeonLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Scale, ArrowUpDown, Shield, Plus, Pencil, Trash2 } from 'lucide-react';

interface CatalogEntry {
  cpt: string;
  descriptor: string;
  category: string;
  default_rank_weight: number;
  is_commonly_secondary: boolean;
  specialty_tags: string[];
}

interface PairRule {
  id: string;
  specialty: string;
  cpt_a: string;
  cpt_b: string;
  primary_when_together: string;
  reason: string;
  active: boolean;
}

interface EditPairRule {
  id: string;
  cpt_a: string;
  cpt_b: string;
  primary_when_together: string;
  reason: string;
}

interface PayerRule {
  id: string;
  payer_plan_id: string;
  rule_type: string;
  match_json: { cpt_codes?: string[] };
  primary_cpt: string | null;
  reason: string;
  active: boolean;
}

export default function CptPrimaryRankingPage() {
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [pairRules, setPairRules] = useState<PairRule[]>([]);
  const [payerRules, setPayerRules] = useState<PayerRule[]>([]);
  const [editingWeight, setEditingWeight] = useState<{ cpt: string; weight: number } | null>(null);
  const [showPairDialog, setShowPairDialog] = useState(false);
  const [showPayerDialog, setShowPayerDialog] = useState(false);
  const [editingPair, setEditingPair] = useState<EditPairRule | null>(null);
  const [pairForm, setPairForm] = useState({ cpt_a: '', cpt_b: '', primary_when_together: '', reason: '' });
  const [payerForm, setPayerForm] = useState({ payer_plan_id: '', cpt_codes: '', primary_cpt: '', reason: '' });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    const [c, p, pr] = await Promise.all([
      supabase.from('cpt_catalog').select('*').order('default_rank_weight', { ascending: false }),
      supabase.from('cpt_pair_rules').select('*').order('created_at', { ascending: false }),
      supabase.from('payer_plan_cpt_rules').select('*').order('created_at', { ascending: false }),
    ]);
    if (c.data) setCatalog(c.data as CatalogEntry[]);
    if (p.data) setPairRules(p.data as PairRule[]);
    if (pr.data) setPayerRules(pr.data as PayerRule[]);
  };

  const updateWeight = async () => {
    if (!editingWeight) return;
    const { error } = await supabase
      .from('cpt_catalog')
      .update({ default_rank_weight: editingWeight.weight })
      .eq('cpt', editingWeight.cpt);
    if (error) { toast.error('Failed to update weight'); return; }
    toast.success(`Weight updated for ${editingWeight.cpt}`);
    setEditingWeight(null);
    loadAll();
  };

  const togglePairRule = async (id: string, active: boolean) => {
    await supabase.from('cpt_pair_rules').update({ active: !active }).eq('id', id);
    loadAll();
  };

  const deletePairRule = async (id: string) => {
    await supabase.from('cpt_pair_rules').delete().eq('id', id);
    toast.success('Pair rule deleted');
    loadAll();
  };

  const updatePairRule = async () => {
    if (!editingPair) return;
    const { error } = await supabase.from('cpt_pair_rules').update({
      cpt_a: editingPair.cpt_a.trim(),
      cpt_b: editingPair.cpt_b.trim(),
      primary_when_together: editingPair.primary_when_together.trim(),
      reason: editingPair.reason.trim(),
    }).eq('id', editingPair.id);
    if (error) { toast.error('Failed to update pair rule'); return; }
    toast.success('Pair rule updated');
    setEditingPair(null);
    loadAll();
  };

  const addPairRule = async () => {
    const { error } = await supabase.from('cpt_pair_rules').insert({
      specialty: 'Orthopedics',
      cpt_a: pairForm.cpt_a.trim(),
      cpt_b: pairForm.cpt_b.trim(),
      primary_when_together: pairForm.primary_when_together.trim(),
      reason: pairForm.reason.trim(),
    });
    if (error) { toast.error('Failed to add pair rule'); return; }
    toast.success('Pair rule added');
    setShowPairDialog(false);
    setPairForm({ cpt_a: '', cpt_b: '', primary_when_together: '', reason: '' });
    loadAll();
  };

  const togglePayerRule = async (id: string, active: boolean) => {
    await supabase.from('payer_plan_cpt_rules').update({ active: !active }).eq('id', id);
    loadAll();
  };

  const deletePayerRule = async (id: string) => {
    await supabase.from('payer_plan_cpt_rules').delete().eq('id', id);
    toast.success('Payer rule deleted');
    loadAll();
  };

  const addPayerRule = async () => {
    const cptCodes = payerForm.cpt_codes.split(',').map(c => c.trim()).filter(Boolean);
    const { error } = await supabase.from('payer_plan_cpt_rules').insert({
      payer_plan_id: payerForm.payer_plan_id.trim(),
      rule_type: 'primary_override',
      match_json: { cpt_codes: cptCodes },
      primary_cpt: payerForm.primary_cpt.trim(),
      reason: payerForm.reason.trim(),
    });
    if (error) { toast.error('Failed to add payer rule'); return; }
    toast.success('Payer rule added');
    setShowPayerDialog(false);
    setPayerForm({ payer_plan_id: '', cpt_codes: '', primary_cpt: '', reason: '' });
    loadAll();
  };

  return (
    <SurgeonLayout>
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Scale className="w-6 h-6" />
            CPT Primary Ranking Engine
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configure deterministic CPT submission ordering for prior authorization
          </p>
        </div>

        <Tabs defaultValue="weights">
          <TabsList>
            <TabsTrigger value="weights">
              <ArrowUpDown className="w-3.5 h-3.5 mr-1.5" />
              Category Weights
            </TabsTrigger>
            <TabsTrigger value="pairs">
              <Scale className="w-3.5 h-3.5 mr-1.5" />
              Pair Rules
            </TabsTrigger>
            <TabsTrigger value="payer">
              <Shield className="w-3.5 h-3.5 mr-1.5" />
              Payer Overrides
            </TabsTrigger>
          </TabsList>

          {/* Category Weights Tab */}
          <TabsContent value="weights">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">CPT Catalog — Rank Weights</CardTitle>
                <CardDescription>Higher weight = more likely to be ranked as primary. Range: 0–100.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>CPT</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-center">Weight</TableHead>
                      <TableHead className="text-center">Secondary?</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {catalog.map(entry => (
                      <TableRow key={entry.cpt}>
                        <TableCell className="font-mono font-medium">{entry.cpt}</TableCell>
                        <TableCell className="text-sm">{entry.descriptor}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px]">{entry.category}</Badge>
                        </TableCell>
                        <TableCell className="text-center font-mono">{entry.default_rank_weight}</TableCell>
                        <TableCell className="text-center">
                          {entry.is_commonly_secondary && <Badge variant="secondary" className="text-[10px]">Yes</Badge>}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => setEditingWeight({ cpt: entry.cpt, weight: entry.default_rank_weight })}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pair Rules Tab */}
          <TabsContent value="pairs">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base">CPT Pair Rules</CardTitle>
                  <CardDescription>When two CPTs appear together, which is primary?</CardDescription>
                </div>
                <Button size="sm" onClick={() => setShowPairDialog(true)}>
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Add Pair Rule
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>CPT A</TableHead>
                      <TableHead>CPT B</TableHead>
                      <TableHead>Primary</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead className="text-center">Active</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pairRules.map(rule => (
                      <TableRow key={rule.id}>
                        <TableCell className="font-mono">{rule.cpt_a}</TableCell>
                        <TableCell className="font-mono">{rule.cpt_b}</TableCell>
                        <TableCell>
                          <Badge className="font-mono text-[10px]">{rule.primary_when_together}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[300px] truncate">{rule.reason}</TableCell>
                        <TableCell className="text-center">
                          <Switch checked={rule.active} onCheckedChange={() => togglePairRule(rule.id, rule.active)} />
                        </TableCell>
                        <TableCell className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingPair({ id: rule.id, cpt_a: rule.cpt_a, cpt_b: rule.cpt_b, primary_when_together: rule.primary_when_together, reason: rule.reason })}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deletePairRule(rule.id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payer Overrides Tab */}
          <TabsContent value="payer">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base">Payer Plan Overrides</CardTitle>
                  <CardDescription>Payer-specific CPT ordering rules (highest priority)</CardDescription>
                </div>
                <Button size="sm" onClick={() => setShowPayerDialog(true)}>
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Add Payer Rule
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Payer Plan ID</TableHead>
                      <TableHead>Match CPTs</TableHead>
                      <TableHead>Primary CPT</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead className="text-center">Active</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payerRules.map(rule => (
                      <TableRow key={rule.id}>
                        <TableCell className="font-mono text-xs">{rule.payer_plan_id}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {(rule.match_json?.cpt_codes || []).join(', ')}
                        </TableCell>
                        <TableCell>
                          <Badge className="font-mono text-[10px]">{rule.primary_cpt}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[250px] truncate">{rule.reason}</TableCell>
                        <TableCell className="text-center">
                          <Switch checked={rule.active} onCheckedChange={() => togglePayerRule(rule.id, rule.active)} />
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deletePayerRule(rule.id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Weight Dialog */}
        <Dialog open={!!editingWeight} onOpenChange={(v) => { if (!v) setEditingWeight(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Rank Weight — {editingWeight?.cpt}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Label>Weight (0–100)</Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={editingWeight?.weight ?? 50}
                onChange={e => setEditingWeight(prev => prev ? { ...prev, weight: Number(e.target.value) } : null)}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingWeight(null)}>Cancel</Button>
              <Button onClick={updateWeight}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Pair Rule Dialog */}
        <Dialog open={showPairDialog} onOpenChange={setShowPairDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add CPT Pair Rule</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>CPT A</Label>
                  <Input value={pairForm.cpt_a} onChange={e => setPairForm(p => ({ ...p, cpt_a: e.target.value }))} placeholder="27427" />
                </div>
                <div>
                  <Label>CPT B</Label>
                  <Input value={pairForm.cpt_b} onChange={e => setPairForm(p => ({ ...p, cpt_b: e.target.value }))} placeholder="29881" />
                </div>
              </div>
              <div>
                <Label>Primary When Together</Label>
                <Input value={pairForm.primary_when_together} onChange={e => setPairForm(p => ({ ...p, primary_when_together: e.target.value }))} placeholder="27427" />
              </div>
              <div>
                <Label>Reason</Label>
                <Textarea value={pairForm.reason} onChange={e => setPairForm(p => ({ ...p, reason: e.target.value }))} placeholder="ACL reconstruction takes primary over meniscectomy" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPairDialog(false)}>Cancel</Button>
              <Button onClick={addPairRule} disabled={!pairForm.cpt_a || !pairForm.cpt_b || !pairForm.primary_when_together || !pairForm.reason}>Add Rule</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Payer Rule Dialog */}
        <Dialog open={showPayerDialog} onOpenChange={setShowPayerDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Payer Override</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Payer Plan ID</Label>
                <Input value={payerForm.payer_plan_id} onChange={e => setPayerForm(p => ({ ...p, payer_plan_id: e.target.value }))} placeholder="BCBS-PPO-001" />
              </div>
              <div>
                <Label>Match CPT Codes (comma-separated)</Label>
                <Input value={payerForm.cpt_codes} onChange={e => setPayerForm(p => ({ ...p, cpt_codes: e.target.value }))} placeholder="27427, 29881" />
              </div>
              <div>
                <Label>Primary CPT</Label>
                <Input value={payerForm.primary_cpt} onChange={e => setPayerForm(p => ({ ...p, primary_cpt: e.target.value }))} placeholder="27427" />
              </div>
              <div>
                <Label>Reason</Label>
                <Textarea value={payerForm.reason} onChange={e => setPayerForm(p => ({ ...p, reason: e.target.value }))} placeholder="BCBS requires reconstruction as primary" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPayerDialog(false)}>Cancel</Button>
              <Button onClick={addPayerRule} disabled={!payerForm.payer_plan_id || !payerForm.cpt_codes || !payerForm.primary_cpt || !payerForm.reason}>Add Rule</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Pair Rule Dialog */}
        <Dialog open={!!editingPair} onOpenChange={(v) => { if (!v) setEditingPair(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit CPT Pair Rule</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>CPT A</Label>
                  <Input value={editingPair?.cpt_a ?? ''} onChange={e => setEditingPair(p => p ? { ...p, cpt_a: e.target.value } : null)} />
                </div>
                <div>
                  <Label>CPT B</Label>
                  <Input value={editingPair?.cpt_b ?? ''} onChange={e => setEditingPair(p => p ? { ...p, cpt_b: e.target.value } : null)} />
                </div>
              </div>
              <div>
                <Label>Primary When Together</Label>
                <Input value={editingPair?.primary_when_together ?? ''} onChange={e => setEditingPair(p => p ? { ...p, primary_when_together: e.target.value } : null)} />
              </div>
              <div>
                <Label>Reason</Label>
                <Textarea value={editingPair?.reason ?? ''} onChange={e => setEditingPair(p => p ? { ...p, reason: e.target.value } : null)} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingPair(null)}>Cancel</Button>
              <Button onClick={updatePairRule}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SurgeonLayout>
  );
}
